import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { KpiCard } from '@/components/KpiCard';
import { ChartCard, EmptyState } from '@/components/ChartCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ProviderChip } from '@/components/ProviderChip';
import { formatNumber, formatPct, formatSentiment, relativeTime } from '@/lib/format';
import { Activity, Sparkles, MessageSquare, FolderKanban, ArrowRight, ScanLine, Plus } from 'lucide-react';
import { toast } from 'sonner';

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/dashboard');
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Loading your AI visibility overview…</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-32 rounded-[var(--radius)]" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-0.5">All your brand projects at a glance.</p>
        </div>
        <Button onClick={() => navigate('/app/projects')} data-testid="dashboard-new-project-button">
          <Plus className="h-4 w-4 mr-1.5" /> New project
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="dashboard-kpi-projects" label="Projects" value={formatNumber(data.total_projects)} hint="Brands tracked" icon={FolderKanban} />
        <KpiCard testId="dashboard-kpi-scans" label="Scans" value={formatNumber(data.total_scans)} hint={`${formatNumber(data.total_results)} answers analyzed`} icon={ScanLine} />
        <KpiCard testId="dashboard-kpi-mention-rate" label="Mention rate" value={formatPct(data.mention_rate)} hint={`${formatNumber(data.brand_mentions)} mentions`} icon={Activity} />
        <KpiCard testId="dashboard-kpi-sentiment" label="Avg sentiment" value={formatSentiment(data.avg_sentiment)} hint="Across all providers" icon={Sparkles} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <ChartCard title="Your projects" subtitle="Ranked by AI mention rate" className="lg:col-span-7" testId="dashboard-projects-card">
          {data.projects && data.projects.length > 0 ? (
            <div className="space-y-3">
              {data.projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/app/projects/${p.id}`}
                  data-testid={`dashboard-project-row-${p.id}`}
                  className="flex items-center justify-between rounded-lg border bg-secondary/40 hover:bg-secondary p-3 transition-colors"
                >
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-muted-foreground">{p.domain || p.industry || '—'} · {formatNumber(p.runs)} answers</div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Mention rate</div>
                      <div className="font-display font-semibold tabular">{formatPct(p.mention_rate)}</div>
                    </div>
                    <div className="text-right hidden sm:block">
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Sentiment</div>
                      <div className="font-display font-semibold tabular">{p.avg_sentiment?.toFixed(2)}</div>
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No projects yet"
              description="Create your first brand project to start tracking AI visibility."
              action={<Button onClick={() => navigate('/app/projects')} data-testid="dashboard-empty-create-button">Create project</Button>}
            />
          )}
        </ChartCard>

        <ChartCard title="Recent scans" subtitle="Latest visibility runs" className="lg:col-span-5" testId="dashboard-recent-scans-card">
          {data.recent_scans && data.recent_scans.length > 0 ? (
            <div className="space-y-2">
              {data.recent_scans.map((s) => (
                <Link
                  key={s.id}
                  to={`/app/projects/${s.project_id}?tab=scans&scan=${s.id}`}
                  className="flex items-center justify-between rounded-lg border p-2.5 hover:bg-secondary transition-colors"
                  data-testid={`dashboard-recent-scan-${s.id}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{s.project_name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{relativeTime(s.created_at)} · {formatNumber(s.total_units)} units</div>
                  </div>
                  <Badge variant={s.status === 'completed' ? 'default' : s.status === 'failed' ? 'destructive' : 'secondary'} className="capitalize">{s.status}</Badge>
                </Link>
              ))}
            </div>
          ) : (
            <EmptyState title="No scans yet" description="Run your first scan from a project to see results here." />
          )}
        </ChartCard>
      </div>

      <ChartCard title="Live providers" subtitle="Currently supported AI answer engines" testId="dashboard-providers-card">
        <div className="flex flex-wrap gap-2">
          <ProviderChip providerKey="openai" active />
          <ProviderChip providerKey="anthropic" active />
          <ProviderChip providerKey="gemini" active />
          <ProviderChip providerKey="perplexity" comingSoon />
          <ProviderChip providerKey="copilot" comingSoon />
          <ProviderChip providerKey="google_ai_overview" comingSoon />
        </div>
      </ChartCard>
    </div>
  );
}
